import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../Api';
import UserTimezoneSelect from '../UserTimezoneSelect';

export default function SettingsProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get('/auth/user-info');
        if (cancelled) return;
        setProfile(res.data);
        setTitle(res.data?.title || '');
        setEmail(res.data?.email || '');
        const preferred = res.data?.themePreference;
        if (preferred === 'dark' || preferred === 'light') {
          setTheme(preferred);
        } else {
          setTheme(localStorage.getItem('darkMode') === 'true' ? 'dark' : 'light');
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await API.put('/auth/profile', { title, email });
      await API.post('/auth/update-theme', { theme });
      localStorage.setItem('darkMode', theme === 'dark' ? 'true' : 'false');
      document.documentElement.classList.toggle('dark', theme === 'dark');
      setSuccess('Profile saved');
      setProfile((prev) => ({ ...prev, title, email, themePreference: theme }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="settings-panel">Loading profile…</div>;
  }

  return (
    <div className="settings-panel">
      <h2>Profile</h2>
      <p className="settings-subtitle">Personal settings for your Commray account.</p>
      {error && <div className="settings-alert settings-alert--error">{error}</div>}
      {success && <div className="settings-alert settings-alert--success">{success}</div>}

      <div className="settings-field">
        <label htmlFor="settings-name">Name</label>
        <input id="settings-name" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Your name" />
      </div>
      <div className="settings-field">
        <label>Username</label>
        <div className="settings-readonly">{profile?.username || '—'}</div>
      </div>
      <div className="settings-field">
        <label htmlFor="settings-email">Email</label>
        <input id="settings-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="settings-field">
        <label>Role</label>
        <div className="settings-readonly">
          {profile?.orgRoleLabel || profile?.orgRole || (profile?.role || '').replace(/^ROLE_/, '') || '—'}
        </div>
      </div>
      <div className="settings-field">
        <label>Theme</label>
        <div className="settings-theme-options">
          <label>
            <input type="radio" name="theme" checked={theme === 'light'} onChange={() => setTheme('light')} />
            Light
          </label>
          <label>
            <input type="radio" name="theme" checked={theme === 'dark'} onChange={() => setTheme('dark')} />
            Dark
          </label>
        </div>
      </div>
      <div className="settings-field">
        <label>Time zone</label>
        <UserTimezoneSelect showInnerLabel={false} selectId="settings-profile-tz" embedded />
      </div>

      <div className="settings-actions">
        <button type="button" className="settings-btn settings-btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      <div className="settings-divider" />
      <h2 style={{ fontSize: 20 }}>Change Password</h2>
      <p className="settings-subtitle">Update the password for your personal account.</p>
      <div className="settings-actions">
        <button type="button" className="settings-btn settings-btn--secondary" onClick={() => navigate('/change-password')}>
          Change Password
        </button>
      </div>
    </div>
  );
}
